import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Search,
    Add,
    Link as LinkIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
    fetchPaymentLinks,
    createPaymentLink,
    renewPaymentLink,
    fetchPaymentLinkDetails,
    mapPaymentLinkFromApi,
} from '../../services/paymentLinkService';
import { PaymentLinksTable } from '../Admin/PaymentLinks/PaymentLinkCard';
import { PaymentLinksStats } from '../Admin/PaymentLinks/PaymentLinksStats';
import { fetchCaissierTerminal } from '../../services/posService';
import CashierSidebar from '../../components/cashier/CashierSidebar';
import CashierHeader from '../../components/cashier/CashierHeader';
import { CreatePaymentLinkModal } from '../Admin/PaymentLinks/CreatePaymentLinkModal';
import { PaymentLinkDetailsModal } from '../Admin/PaymentLinks/PaymentLinkDetailsModal';
import { ResendMethodModal } from '../Admin/PaymentLinks/ResendMethodModal';

const STATUS_FILTERS = [
    { id: 'all', label: 'Tous' },
    { id: 'pending', label: 'En attente' },
    { id: 'paid', label: 'Payés' },
    { id: 'cashed', label: 'Collectés' },
    { id: 'expired', label: 'Expirés' }
];

const CashierPaymentLinksPage = () => {
    const { selectedOrganization, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [pge, setPge] = useState(1);
    const [lmt] = useState(10);
    const [sts, setSts] = useState('all');
    const [slug, setSlug] = useState('');
    const [paymentLinks, setPaymentLinks] = useState([]);
    const [prevPage, setPrevPage] = useState(null);
    const [nextPage, setNextPage] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [caissierTerminal, setCaissierTerminal] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [fromCustomer, setFromCustomer] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedLink, setSelectedLink] = useState(null);
    const [showResendModal, setShowResendModal] = useState(false);
    const [linkToResend, setLinkToResend] = useState(null);

    const loadCaissierTerminal = async () => {
        if (!selectedOrganization?.id || !user?.id) return;
        try {
            const terminal = await fetchCaissierTerminal(selectedOrganization.id, user.id);
            setCaissierTerminal(terminal || null);
        } catch (e) {
            setCaissierTerminal(null);
        }
    };

    const loadPaymentLinks = async () => {
        if (!selectedOrganization?.id) return;
        try {
            setLoading(true);
            const response = await fetchPaymentLinks({
                pge,
                lmt,
                sts: sts === 'all' ? 'all' : sts,
                org: selectedOrganization.id,
                emt: user?.id,
                cmr: 'all',
                slg: slug || ''
            });
            const mapped = (response.contents || []).map(mapPaymentLinkFromApi).filter(Boolean);
            setPaymentLinks(mapped);
            setPrevPage(response.previousPage ?? null);
            setNextPage(response.nextPage ?? null);
        } catch (error) {
            console.error('Error loading payment links:', error);
            setPaymentLinks([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedOrganization?.id && user?.id) loadCaissierTerminal();
    }, [selectedOrganization?.id, user?.id]);

    useEffect(() => {
        if (selectedOrganization?.id) loadPaymentLinks();
    }, [selectedOrganization?.id, sts, slug, pge]);

    useEffect(() => {
        const customer = location.state?.customer;
        if (customer) {
            setFromCustomer(customer);
            setShowCreateModal(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.customer, location.pathname, navigate]);

    const handleSearch = (v) => {
        setSearchTerm(v);
        setSlug(v);
        setPge(1);
    };

    const linkStats = useMemo(() => {
        const total = paymentLinks.length;
        const pending = paymentLinks.filter((l) => l.status === 'pending').length;
        const paid = paymentLinks.filter((l) => l.status === 'paid').length;
        const collected = paymentLinks.filter((l) => l.status === 'collected').length;
        const expired = paymentLinks.filter((l) => l.status === 'expired').length;
        return { total, pending, paid, collected, expired };
    }, [paymentLinks]);

    const handleCreatePaymentLink = async (paymentLinkData) => {
        if (!selectedOrganization?.id) {
            alert('Organisation non sélectionnée');
            return;
        }
        try {
            const data = {
                ...paymentLinkData,
                company_id: paymentLinkData.company_id || selectedOrganization.id,
            };
            await createPaymentLink(data);
            await loadPaymentLinks();
            setShowCreateModal(false);
            const method = paymentLinkData.method || 'sms';
            const methodLabel = { sms: 'SMS', email: 'Email', whatsapp: 'WhatsApp' }[method] || method;
            const customer = paymentLinkData.customer;
            const dest = method === 'email' ? (customer?.email || '') : (customer ? `${customer.zip || ''} ${customer.phoneNumber || ''}`.trim() : '');
            alert(`Lien de paiement créé et envoyé par ${methodLabel} à ${customer?.name || dest || 'le client'}.`);
        } catch (error) {
            console.error('Error creating payment link:', error);
            alert(error.message || 'Erreur lors de la création du lien de paiement');
        }
    };

    const handleViewDetails = async (link) => {
        try {
            const details = await fetchPaymentLinkDetails(link.id);
            setSelectedLink(mapPaymentLinkFromApi({ ...link, ...(details || {}) }));
            setShowDetailsModal(true);
        } catch (error) {
            console.error('Error loading link details:', error);
            setSelectedLink(mapPaymentLinkFromApi(link));
            setShowDetailsModal(true);
        }
    };

    const handleCopyLink = async (link) => {
        try {
            const linkUrl = link.link || `${window.location.origin}/pay/${link.id}`;
            await navigator.clipboard.writeText(linkUrl);
            alert('Lien copié dans le presse-papiers !');
        } catch (error) {
            console.error('Error copying link:', error);
            alert('Erreur lors de la copie du lien');
        }
    };

    const handleRenewLink = async (link) => {
        try {
            const response = await renewPaymentLink(link.id);
            if (response?.error) {
                alert(response.message || 'Erreur lors du renouvellement du lien');
                return;
            }
            alert('Lien renouvelé avec succès !');
            await loadPaymentLinks();
            if (showDetailsModal && selectedLink?.id === link.id) {
                const details = await fetchPaymentLinkDetails(link.id);
                setSelectedLink(mapPaymentLinkFromApi({ ...link, ...(details || {}) }));
            }
        } catch (error) {
            console.error('Error renewing link:', error);
            alert(error.message || 'Erreur lors du renouvellement du lien');
        }
    };

    const handleResendLink = (link) => {
        setLinkToResend(link);
        setShowResendModal(true);
    };

    const handleCancelLink = async (link) => {
        if (!window.confirm('Êtes-vous sûr de vouloir annuler ce lien de paiement ?')) return;
        try {
            setPaymentLinks(prev => prev.map(pl =>
                pl.id === link.id ? { ...pl, status: 'expired' } : pl
            ));
            if (showDetailsModal && selectedLink?.id === link.id) {
                setSelectedLink(prev => prev ? { ...prev, status: 'expired' } : null);
            }
            alert('Lien annulé');
        } catch (error) {
            console.error('Error cancelling link:', error);
            alert('Erreur lors de l\'annulation du lien');
        }
    };

    const handleResendSent = () => {
        setShowResendModal(false);
        setLinkToResend(null);
        loadPaymentLinks();
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/20 to-slate-50 flex">
            <CashierSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden flex flex-col lg:ml-[var(--cashier-sidebar-w,18rem)] transition-[margin] duration-300">
                <CashierHeader
                    onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                    onRefresh={loadPaymentLinks}
                    loading={loading}
                    terminal={caissierTerminal}
                />

                <div className="flex-1 p-5 lg:p-8 overflow-y-auto">
                    {/* Bannière */}
                    <div className="mb-8">
                        <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl border border-amber-200/50 shadow-xl shadow-amber-500/5 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 via-orange-400/5 to-transparent pointer-events-none" />
                            <div className="relative px-6 lg:px-8 py-6">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                                    <div className="flex items-center gap-5">
                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30 ring-4 ring-amber-500/10">
                                            <LinkIcon className="text-white text-3xl" />
                                        </div>
                                        <div>
                                            <h1 className="text-2xl lg:text-3xl font-bold text-slate-800 tracking-tight">Liens de paiement</h1>
                                            <p className="text-slate-600 mt-1">Générez et gérez vos liens de paiement instantanés</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateModal(true)}
                                        disabled={!caissierTerminal?.id}
                                        className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-300 shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Add className="text-xl" />
                                        <span>Nouveau lien</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filtres : une seule ligne dès sm (statuts défilants), empilés sur téléphone */}
                    <div className="bg-white/95 backdrop-blur-sm rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 p-4 sm:p-5 mb-8">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative w-full sm:w-48 lg:w-64 shrink-0">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Rechercher un lien..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    className="w-full pl-10 pr-3 py-2.5 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all bg-white/80 text-sm font-medium"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:flex-1 sm:min-w-0 sm:overflow-x-auto">
                                {STATUS_FILTERS.map((f) => (
                                    <button
                                        key={f.id}
                                        type="button"
                                        onClick={() => { setSts(f.id); setPge(1); }}
                                        className={`shrink-0 px-3 lg:px-4 py-2 rounded-xl whitespace-nowrap text-sm font-semibold transition-all duration-200 ${
                                            sts === f.id
                                                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/25'
                                                : 'text-slate-600 hover:text-amber-600 hover:bg-amber-50 border-2 border-slate-200 hover:border-amber-200'
                                        }`}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Liste */}
                    {loading && paymentLinks.length === 0 ? (
                        <div className="bg-white/95 backdrop-blur-sm rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 p-12 text-center">
                            <div className="w-14 h-14 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-5" />
                            <p className="text-slate-600 font-medium">Chargement des liens...</p>
                        </div>
                    ) : paymentLinks.length === 0 ? (
                        <div className="bg-white/95 backdrop-blur-sm rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 p-16 text-center">
                            <div className="w-24 h-24 bg-gradient-to-br from-slate-100 to-amber-50 rounded-3xl flex items-center justify-center mx-auto mb-6 border-2 border-dashed border-slate-300">
                                <LinkIcon className="text-slate-400 text-4xl" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-800 mb-3">
                                {searchTerm || sts !== 'all' ? 'Aucun lien trouvé' : 'Aucun lien de paiement'}
                            </h3>
                            <p className="text-slate-600 max-w-md mx-auto mb-6">
                                {searchTerm || sts !== 'all'
                                    ? 'Aucun lien ne correspond à vos critères.'
                                    : 'Créez votre premier lien de paiement pour commencer.'}
                            </p>
                            {!searchTerm && sts === 'all' && (
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(true)}
                                    disabled={!caissierTerminal?.id}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-500/25 disabled:opacity-50"
                                >
                                    <Add className="text-lg" />
                                    Nouveau lien
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="mb-8">
                                <PaymentLinksStats stats={linkStats} />
                            </div>

                            <div className="mb-8">
                                <PaymentLinksTable
                                    links={paymentLinks}
                                    onViewDetails={handleViewDetails}
                                    onCopyLink={handleCopyLink}
                                    onResendLink={handleResendLink}
                                    onCancelLink={handleCancelLink}
                                    hidePagination
                                />
                            </div>

                            {(nextPage != null || prevPage != null) && paymentLinks.length > 0 && (
                                <div className="bg-white rounded-2xl px-4 py-3 border border-slate-200 shadow-sm">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <p className="text-sm text-slate-600">
                                            {paymentLinks.length} lien(s) · Page {pge}
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => prevPage != null && setPge(prevPage)}
                                                disabled={prevPage == null}
                                                className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                Précédent
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => nextPage != null && setPge(nextPage)}
                                                disabled={nextPage == null}
                                                className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                Suivant
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <CreatePaymentLinkModal
                show={showCreateModal}
                onClose={() => { setShowCreateModal(false); setFromCustomer(null); }}
                onCreate={handleCreatePaymentLink}
                terminals={caissierTerminal ? [caissierTerminal] : []}
                initialCustomer={fromCustomer}
            />

            <PaymentLinkDetailsModal
                show={showDetailsModal}
                onClose={() => { setShowDetailsModal(false); setSelectedLink(null); }}
                link={selectedLink}
                onCopyLink={handleCopyLink}
                onRenewLink={handleRenewLink}
                onResendLink={handleResendLink}
                onCancelLink={handleCancelLink}
            />

            <ResendMethodModal
                show={showResendModal}
                onClose={() => { setShowResendModal(false); setLinkToResend(null); }}
                link={linkToResend}
                onSent={handleResendSent}
            />
        </div>
    );
};

export default CashierPaymentLinksPage;
